"use client";

import React, {useMemo, useState} from "react";
import VadImage from "vadimages-nextjs-image-optimizer";
import {generateGoogleCalendarEvent} from "@/helpers/generateEvent";
import {CALENDAR_COPY, EVENT_END, EVENT_START, EventPartner, commanderName, commanderParts} from "./content";
import {GooFilter} from "./parts/Blobs";
import Briefing from "./parts/Briefing";
import BootPanel from "./parts/BootPanel";
import LaunchVideo from "./parts/LaunchVideo";
import NamePanel from "./parts/NamePanel";
import {Lockup, TransmissionFooter} from "./parts/Chrome";
import {MissionPhase, prefersReducedMotion, useBootProgress, useCountdown, useLaunchHold} from "./parts/useMission";
import {useMissionProgress} from "./parts/usePreload";
import css from "./mjbc-2026.module.scss";

const IMG = '/images/mjbc-2026';

interface MissionSequenceProps {
    partner?: EventPartner;
}

/**
 * "Save the Date" — DIRECT LINK frames 1-9.
 *
 * boot (1-4) -> nameplate (5) -> rocket launch (6-7) -> briefing over the still
 * -running launch (8) -> briefing settled on the circuit art, footer in (9).
 */
export default function MissionSequence({partner}: MissionSequenceProps) {
    const [phase, setPhase] = useState<MissionPhase>('boot');
    // Frame 8 becomes frame 9 once the launch finishes: the backdrop settles and
    // the transmission footer arrives.
    const [settled, setSettled] = useState(false);

    // The bar is a real loading bar: it fills as the backdrops, the badge and
    // the rocket clip arrive, and does not hand over to the nameplate until
    // they have.
    const loaded = useMissionProgress('save-the-date');
    const boot = useBootProgress(phase === 'boot', loaded, () => setPhase('intended'));
    useLaunchHold(phase === 'launch', () => {
        setPhase('briefing');
        // Reduced motion shows a still rather than a clip, so nothing will ever
        // report the launch as finished — settle to frame 9 immediately.
        if (prefersReducedMotion()) setSettled(true);
    });

    const countdown = useCountdown(EVENT_START);
    const name = commanderName(partner);

    // Google only: the design draws a single button, and the site's three-way
    // AddEventButton picker belongs to the calendar page's layout, not here.
    // Which is also why the description may carry HTML — see CALENDAR_COPY.
    const calendarUrl = useMemo(() => generateGoogleCalendarEvent({
        text: CALENDAR_COPY.title,
        start: EVENT_START,
        end: EVENT_END,
        details: CALENDAR_COPY.description,
    }), []);

    // The clip stays mounted through the briefing so it can dissolve out under
    // the copy instead of disappearing in one frame.
    const launching = phase === 'launch' || phase === 'briefing';
    // The line art belongs to frame 9 and arrives with the copy. Frames 7 and 8
    // are the teal plate and the exhaust with no circuit at all.
    const onCircuit = phase === 'briefing';

    return (
        <div className={css.stage}>
            {/* Referenced by id from the stylesheet, so it has to be in the
                document — mounted once here rather than inside each button. */}
            <GooFilter/>

            <div className={css.backdrop} aria-hidden="true">
                {/* The teal plate stays for the whole sequence; the circuit art
                    is laid over it rather than swapped in. Frame 9 is both. */}
                <VadImage
                    src={`${IMG}/bg-teal-gradient-v2.png`}
                    alt=""
                    width={1441}
                    height={811}
                    className={css.backdropImage}
                    priority
                />

                {onCircuit ? <div className={css.backdropCircuit}/> : null}

            </div>

            {/* The loader owns the screen by itself. The lockup is the reward
                for the bar filling — it arrives with the nameplate and stays
                for the rest of the sequence. */}
            {phase !== 'boot' ? <Lockup/> : null}

            {phase === 'boot' ? <BootPanel {...boot}/> : null}
            {phase === 'intended' ? <NamePanel parts={commanderParts(partner)} onContinue={() => setPhase('launch')}/> : null}

            {phase === 'briefing' ? (
                <Briefing
                    stage="save-the-date"
                    countdown={countdown}
                    action={{kind: 'link', href: calendarUrl}}
                />
            ) : null}

            {phase === 'briefing' && settled ? <TransmissionFooter name={name}/> : null}

            {/* Last, and outside the backdrop: the launch renders over the
                lockup and over the invite, which is how frames 7 and 8 are
                drawn. .backdrop carries a z-index, so a child of it could never
                escape to sit above them. */}
            {launching ? <LaunchVideo settled={settled} onSettled={() => setSettled(true)}/> : null}

            {/* The sequence is driven by client state, so without JS the page
                would sit on a progress bar that never fills. These invites go out
                over show-floor connections, so degrade to the thing that actually
                matters — who it is for, when, where, and the calendar link.
                Browsers do not fetch resources inside <noscript> when scripting is
                on, so this costs enabled visitors nothing. */}
            <noscript>
                {/* The lockup is tied to a phase that never advances without
                    JS, so it has to be re-stated here or these visitors get an
                    unbranded page. */}
                <Lockup/>
                <Briefing
                    stage="save-the-date"
                    countdown={countdown}
                    action={{kind: 'link', href: calendarUrl}}
                />
                <TransmissionFooter name={name}/>
            </noscript>
        </div>
    );
}
